import { useForm, zodResolver } from '@mantine/form';
import numeral from 'numeral';
import { useState } from 'react';

import { Declaration, DeclarationGoods } from '@e-origin/shared';

import { useDeclarationService } from '../../../services';
import { DeclarationViewGoodLVFormValues, declarationViewGoodLVSchema } from './validateDeclarationViewGoodLVForm';

interface UseDeclarationViewGoodLVFormProps {
  declaration: Declaration;
  goodDetails: DeclarationGoods;
  onHide(): void;
}

export const useDeclarationViewGoodLVForm = ({ declaration, goodDetails, onHide }: UseDeclarationViewGoodLVFormProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const { saveMultipleGoods } = useDeclarationService();

  const form = useForm<DeclarationViewGoodLVFormValues>({
    initialValues: {
      _id: goodDetails?._id || '',
      referenceNumberUCR: goodDetails?.referenceNumberUCR || '',
      additionalProcedure: goodDetails?.additionalProcedure || [],
      descriptionOfGoods: goodDetails?.descriptionOfGoods || '',
      commodityCode: {
        hsCode: goodDetails?.commodityCode?.hsCode?.trim() || '',
      },
      goodsMeasure: {
        intrinsicValue: goodDetails?.goodsMeasure?.intrinsicValue ? Number(numeral(goodDetails?.goodsMeasure?.intrinsicValue).format('0.00')) : 0,
        currencyCode: goodDetails?.goodsMeasure?.currencyCode || '',
        grossMass: goodDetails?.goodsMeasure?.grossMass || 0,
        supplementaryUnits: goodDetails?.goodsMeasure?.supplementaryUnits || 0,
      },
      transportInsurance: {
        currencyCode: goodDetails?.transportInsurance?.currencyCode || 'EUR',
        amount: goodDetails?.transportInsurance?.amount ? Number(numeral(goodDetails?.transportInsurance?.amount).format('0.00')) : 0,
      },
      packaging: {
        numberOfPackages: goodDetails?.packaging?.numberOfPackages || 0,
      },
      dutiesAndTaxes: {
        methodOfPayment: goodDetails?.dutiesAndTaxes?.items?.length ? goodDetails?.dutiesAndTaxes.items[0].methodOfPayment : '',
      },
    },
    validate: zodResolver(declarationViewGoodLVSchema),
    validateInputOnBlur: true,
  });

  const onSubmit = async (values) => {
    setIsSaving(true);
    try {
      const updatedGood: DeclarationGoods = { ...goodDetails };
      const { additionalProcedure, ...restFormFields } = values;
      Object.assign(updatedGood, { ...restFormFields, additionalProcedure: additionalProcedure });

      await saveMultipleGoods({ declaration, updatedGoods: [updatedGood] });
      onHide();
    } finally {
      setIsSaving(false);
    }
  };

  return {
    form,
    onSubmit,
    isSaving,
  };
};
